/* eslint-disable max-len */

import React from 'react';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { Mutation } from 'react-apollo';
import config from 'config';
import amplitude from 'amplitude-js';
import { initializeSentry } from 'lib/sentry';
import { ErrorManagerProvider } from 'context/error-manager-context/main';
import { NoPermissionsError } from 'components/utility/NoPermissionsError';
import { ErrorBoundary } from 'components/utility/error-boundary/error-boundary';
import moment from 'moment';
import { asyncComponent } from 'lib/async-component';
import { detectInitialLocale, loadMessages, setLocale, getLocale } from 'lib/locale';
import { DefaultDigitalData } from 'lib/digital-data';
import updateLocaleMutation from 'update-locale.gql';
import LocaleContext from 'context/locale-context';
import Balance from './routes/dashboard/Balance/Balance';

import './app.css';
import '../node_modules/milkyway/semantic.min.css';
import { isBetaUser } from './utils';
import { withOrganization } from './components/organization-select/withOrganization';

// cache value on the window object to survive path changes
window.ccg = /r=CCG/i.test(window.location.search || '');

const Create = asyncComponent(
  () => import(/* webpackChunkName: "routes-create" */ 'src/routes/create/index.js'),
  'Create',
);
const Manage = asyncComponent(
  () => import(/* webpackChunkName: "routes-manage" */ 'src/routes/manage/index.js'),
  'Manage',
);
const EventSelect = asyncComponent(
  () => import(/* webpackChunkName: "routes-create" */ 'src/routes/create/steps/eventSelect.js'),
  'EventSelect',
);
const Reports = asyncComponent(
  () => import(/* webpackChunkName: "routes-reports" */ 'src/routes/reports/index.js'),
  'Reports',
);

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      locale: null,
      messages: null,
      intervalId: null,
      user: props.user,
    };

    amplitude.getInstance().init(config.amplitude.key);
    initializeSentry(props.user);
  }

  async componentDidMount() {
    const locale = detectInitialLocale();
    const messages = await loadMessages(locale);
    await this.setState({ locale, messages });

    moment.locale(locale);

    const intervalId = window.setInterval(async () => {
      const localStorageLocale = getLocale();
      if (localStorageLocale && localStorageLocale !== this.state.locale) {
        const localStorageMessages = await loadMessages(localStorageLocale);
        this.setState({ locale: localStorageLocale, messages: localStorageMessages });
      }
    }, 1000);
    this.setState({ intervalId });
    setLocale(this.state.locale);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.locale !== prevState.locale) setLocale(this.state.locale);
  }

  componentWillUnmount() {
    clearInterval(this.state.intervalId);
  }

  switchLocale = localeChange => async locale => {
    const messages = await loadMessages(locale);
    this.setState({ locale, messages });
    if (this.state.user) localeChange({ variables: { locale: locale.toUpperCase().replace('-', '_') } });
  };

  render() {
    if (!this.state.messages) return null;

    return (
      <Mutation mutation={updateLocaleMutation}>
        {localeChange => (
          <LocaleContext.Provider
            value={{
              switchLocale: this.switchLocale(localeChange),
              user: this.props.user,
              locale: this.state.locale,
            }}
          >
            <DefaultDigitalData />
            <IntlProvider locale={this.state.locale} messages={this.state.messages}>
              <BrowserRouter>
                <ErrorBoundary>
                  <ErrorManagerProvider>
                    <Switch>
                      <Route
                        exact
                        path="/create"
                        render={props =>
                          isBetaUser(this.props.user) ? <EventSelect {...props} /> : <NoPermissionsError />
                        }
                      />
                      <Route
                        exact
                        path="/create/:listingType/:step?"
                        render={props => {
                          return(isBetaUser(this.props.user) ? <Create {...props} /> : <NoPermissionsError />)
                        }}
                      />
                      <Route exact path="/host/events/:id/:dest/:step?" component={Manage} />
                      <Route exact path="/host/dashboard/balance/:currency" render={data => <Balance currency={data.match.params.currency} user={this.props.user} /> } />
                      <Route exact path={["/host/dashboard/new", "/dashboard"]} component={Reports} />
                    </Switch>
                  </ErrorManagerProvider>
                </ErrorBoundary>
              </BrowserRouter>
            </IntlProvider>
          </LocaleContext.Provider>
        )}
      </Mutation>
    );
  }
}

export default withOrganization(App);
